import CopyBtn from "@/components/copy-btn";
import { useSuperAdminReturnableOrder } from "@babackapp/private-api-client/client/use-super-admin-shops";
import type { SuperAdminReturnableOrderDto } from "@babackapp/private-api-client/generated/schema";
import { Button } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { ExternalLink, Loader2 } from "lucide-react";
import { useState } from "react";

const FIELDS = [
  { key: "orderNumber", label: "Order number" },
  { key: "orderId", label: "Order ID" },
  { key: "customerEmail", label: "Email" },
  { key: "zipCode", label: "Code postal" },
] as const satisfies readonly {
  key: keyof SuperAdminReturnableOrderDto;
  label: string;
}[];

/** Opens the shop's return portal on its storefront (Shopify app proxy). */
function ReturnPortalLink({
  storefrontUrl,
  label,
}: {
  storefrontUrl: string;
  label?: string;
}) {
  return (
    <a
      href={`${storefrontUrl}/a/return`}
      target="_blank"
      rel="noreferrer"
      title="Ouvrir la page retour du shop"
      className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-violet-600"
    >
      {label}
      <ExternalLink className="size-3.5" />
    </a>
  );
}

function FieldRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b py-2 last:border-0">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {value ? (
        <CopyBtn value={value} className="text-sm font-medium">
          {value}
        </CopyBtn>
      ) : (
        <span className="text-sm text-muted-foreground">–</span>
      )}
    </div>
  );
}

function TestOrderFields({ shopId }: { shopId: string }) {
  const { data, isPending } = useSuperAdminReturnableOrder(shopId, true);

  if (isPending) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data?.orderNumber) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        Aucune commande acceptée par le portail parmi les récentes.
      </p>
    );
  }

  return (
    <div>
      {FIELDS.map(({ key, label }) => (
        <FieldRow key={key} label={label} value={data[key] ?? null} />
      ))}
    </div>
  );
}

/**
 * Looks up, on demand, an order of the shop still holding a non-returned item
 * and lays out its portal credentials as copyable fields. Fetching is lazy,
 * and starts only once the dialog opens, because it costs one query per shop.
 */
function TestOrderDialog({ shopId }: { shopId: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Commande test
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Commande test</DialogTitle>
          <DialogDescription>
            Une commande récente du shop que le portail accepte, vérifiée
            contre ses propres règles, à saisir dans le formulaire de retour.
          </DialogDescription>
        </DialogHeader>
        {isOpen && <TestOrderFields shopId={shopId} />}
      </DialogContent>
    </Dialog>
  );
}

export function CustomerPortalTestOrder({
  shopId,
  storefrontUrl,
}: {
  shopId: string;
  storefrontUrl: string | null;
}) {
  return (
    <div className="flex items-center gap-2">
      <TestOrderDialog shopId={shopId} />
      {storefrontUrl && (
        <ReturnPortalLink storefrontUrl={storefrontUrl} label="Portail" />
      )}
    </div>
  );
}
